import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { industriesApi } from './api'
import type { Industry } from './types'

export function useIndustries() {
    return useQuery({
        queryKey: ['industries'],
        queryFn: async () => {
            const res = await industriesApi.list()
            return (res.data?.data ?? res.data) as Industry[]
        },
    })
}

export function useIndustry(id: string) {
    return useQuery({
        queryKey: ['industries', id],
        queryFn: async () => {
            const res = await industriesApi.get(id)
            return res.data as Industry
        },
        enabled: !!id,
    })
}

export function useCreateIndustry() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data: { name: string; code: string; description?: string; icon?: string }) =>
            industriesApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['industries'] })
        },
    })
}

export function useUpdateIndustry() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { name?: string; code?: string; description?: string; icon?: string; is_active?: boolean } }) =>
            industriesApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['industries'] })
        },
    })
}

export function useDeleteIndustry() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => industriesApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['industries'] })
        },
    })
}

export function useSetBenchmarks() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: ({ id, benchmarks }: {
            id: string
            benchmarks: Array<{ domain_id: string; target_level: number; description?: string }>
        }) => industriesApi.setBenchmarks(id, benchmarks),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['industries'] })
        },
    })
}

